/**
 * Partial progress invoices for the 40% pre-shipment and 10% delivery
 * milestones. A slice is the selected structures' share of that milestone.
 * The last unbilled slice is the remainder, so the slices add up to the
 * milestone. The 50% deposit is never split.
 *
 * Pure — no Firebase. The Cloud Function keeps a copy at
 * functions/src/partialInvoiceAllocation.ts.
 */

export type PartialInstallmentId = 'deposit' | 'pre_shipment' | 'final';

export const WHOLE_PROJECT_PACKAGE_KEY = '__whole_project__';

export interface ContractStructure {
  structureId: string;
  structureName: string;
  /** design + timber kit + install, in whole dollars. */
  contractValue: number;
}

export interface BillablePackage {
  key: string;
  label: string;
  structureIds: string[];
  /** Sum of this package's structure contract values. Overlaps are counted here. */
  contractValue: number;
  /** Structures that also sit on another package. */
  sharedStructureIds: string[];
}

export interface PackageGroupInput {
  key: string;
  label: string;
  structureIds: string[];
}

export interface MilestoneInvoiceRef {
  installmentId?: string;
  status?: string;
  structureIds?: string[];
  amount?: number;
  partialId?: string;
  closesMilestone?: boolean;
  quickbooksInvoiceId?: string;
  quickbooksInvoiceNum?: string;
  balance?: number;
  structureGroupKeys?: string[];
  structureGroupLabels?: string[];
  basisAmount?: number;
  /** Structure-line dollars on this invoice, excluding folded change orders. */
  baseBilled?: number;
  /** Change-order references folded into this invoice at their full milestone amount. */
  foldedChangeOrderRefs?: string[];
  createdAt?: unknown;
  paidAt?: unknown;
}

export interface SliceCoAdjustment {
  reference: string;
  title: string;
  amount: number;
}

export interface MilestoneSlice {
  fullMilestone: boolean;
  closesMilestone: boolean;
  partialId?: string;
  structureIds: string[];
  structureGroupKeys: string[];
  structureGroupLabels: string[];
  /** Contract value of the selected structures, each structure once. */
  basisAmount: number;
  /** Dollars to invoice for the contract slice, before bundled immediate change orders. */
  amount: number;
  /** Portion of `amount` that belongs on the structure lines (after change-order lines). */
  baseLineTarget: number;
  coLines: SliceCoAdjustment[];
  includedLabels: string[];
  omittedLabels: string[];
}

export interface AllocateMilestoneSliceArgs {
  installmentId: PartialInstallmentId;
  /** Schedule row base (percent of the contract, before folded change orders). */
  baseAmount: number;
  /** Folded change orders already allocated to this milestone. */
  coAdjustments?: SliceCoAdjustment[];
  /**
   * Change orders to fold into this invoice, at their full milestone amount.
   * Omit the field to include every change order still open on the milestone.
   * An empty list leaves them all off.
   */
  selectedChangeOrderRefs?: string[];
  /** Full milestone dollars (base + folded change orders). */
  milestoneAmount: number;
  structures: ContractStructure[];
  /** Empty means bill the whole milestone, unless change orders are selected on their own. */
  selectedStructureIds: string[];
  existingInvoices: MilestoneInvoiceRef[];
  /** Display names for the groups the staff selected. */
  includedLabels?: string[];
  /** Group keys the staff selected. */
  includedGroupKeys?: string[];
  /** Packages that will not be on this invoice. */
  omittedLabels?: string[];
  /** Short milestone name, e.g. "40% due prior to shipment". */
  milestoneLabel: string;
}

export type AllocateMilestoneSliceResult =
  | { ok: true; slice: MilestoneSlice; memo?: string }
  | { ok: false; error: string };

export function formatInvoiceDollars(n: number): string {
  const rounded = Math.round(n);
  const sign = rounded < 0 ? '-' : '';
  return `${sign}$${Math.abs(rounded).toLocaleString('en-US')}`;
}

export function contractValueFromLine(line: {
  designCost?: number;
  timberKitCost?: number;
  installCost?: number;
  subtotal?: number;
}): number {
  const parts =
    (Number(line.designCost) || 0) +
    (Number(line.timberKitCost) || 0) +
    (Number(line.installCost) || 0);
  if (parts > 0) return Math.round(parts);
  return Math.round(Number(line.subtotal) || 0);
}

export function structuresFromLineItems(
  lines: Array<{
    structureId?: string;
    structureName?: string;
    designCost?: number;
    timberKitCost?: number;
    installCost?: number;
    subtotal?: number;
  }>,
): ContractStructure[] {
  const byId = new Map<string, ContractStructure>();
  for (const line of lines) {
    const structureId = (line.structureId || '').trim();
    if (!structureId) continue;
    const value = contractValueFromLine(line);
    const prev = byId.get(structureId);
    if (prev) {
      prev.contractValue += value;
      continue;
    }
    byId.set(structureId, {
      structureId,
      structureName: (line.structureName || '').trim() || 'Structure',
      contractValue: value,
    });
  }
  return Array.from(byId.values());
}

/** Whole dollars that sum to `target`. Zero weights get nothing. */
export function distributeToTarget(weights: number[], target: number): number[] {
  const total = weights.reduce((s, w) => s + w, 0);
  const tgt = Math.round(target);
  if (total <= 0 || weights.length === 0) return weights.map(() => 0);
  const raw = weights.map((w) => (w / total) * tgt);
  const out = raw.map((r) => Math.floor(r));
  let remainder = tgt - out.reduce((s, v) => s + v, 0);
  const byFrac = raw
    .map((r, i) => ({ i, frac: r - Math.floor(r) }))
    .sort((a, b) => b.frac - a.frac || a.i - b.i);
  for (let k = 0; k < byFrac.length && remainder > 0; k += 1) {
    out[byFrac[k].i] += 1;
    remainder -= 1;
  }
  for (let k = byFrac.length - 1; k >= 0 && remainder < 0; k -= 1) {
    if (out[byFrac[k].i] > 0) {
      out[byFrac[k].i] -= 1;
      remainder += 1;
    }
  }
  return out;
}

export interface PartialInvoiceGroupInput {
  key: string;
  label: string;
  structureIds: string[];
}

export interface PartialInvoiceGroupLine {
  key: string;
  label: string;
  structureIds: string[];
  amount: number;
  itemId: string;
  itemName: string;
}

/**
 * One QuickBooks line per structure group. Structure amounts are weights;
 * the lines sum to the milestone slice.
 */
export function partialGroupInvoiceLines(args: {
  groups: PartialInvoiceGroupInput[];
  lines: Array<{ structureId: string; amount: number; itemId: string; itemName: string }>;
  lineTarget: number;
}): PartialInvoiceGroupLine[] {
  const seen = new Set<string>();
  const usable = args.groups
    .map((group) => {
      const structureIds = group.structureIds
        .map((id) => id.trim())
        .filter((id) => {
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      return { key: group.key.trim(), label: group.label.trim(), structureIds };
    })
    .filter((group) => group.label && group.structureIds.length > 0);
  if (!usable.length) return [];

  const weighted = usable.map((group) => {
    const ids = new Set(group.structureIds);
    const matched = args.lines.filter((line) => ids.has(line.structureId) && line.amount > 0);
    const dominant = matched.slice().sort((a, b) => b.amount - a.amount)[0];
    return {
      group,
      weight: matched.reduce((sum, line) => sum + line.amount, 0),
      itemId: dominant?.itemId || '',
      itemName: dominant?.itemName || '',
    };
  });
  const amounts = distributeToTarget(weighted.map((row) => row.weight), args.lineTarget);
  return weighted
    .map((row, index) => ({
      key: row.group.key,
      label: row.group.label,
      structureIds: row.group.structureIds,
      amount: amounts[index] || 0,
      itemId: row.itemId,
      itemName: row.itemName,
    }))
    .filter((line) => line.amount > 0 && line.itemId);
}

/** Selected groups, with a structure that sits in two groups counted on the first one. */
export function invoiceGroupsFromPackages(
  packages: Array<{ key: string; label: string; structureIds: string[] }>,
  selectedKeys: string[],
): PartialInvoiceGroupInput[] {
  const seen = new Set<string>();
  return packages
    .filter((pkg) => selectedKeys.includes(pkg.key))
    .map((pkg) => {
      const structureIds = pkg.structureIds
        .map((id) => id.trim())
        .filter((id) => {
          if (!id || seen.has(id)) return false;
          seen.add(id);
          return true;
        });
      return { key: pkg.key, label: pkg.label, structureIds };
    })
    .filter((group) => group.structureIds.length > 0);
}

export function invoiceIsVoid(status?: string): boolean {
  return status === 'voided' || status === 'removed_in_qb';
}

/**
 * A whole-milestone invoice, or the last partial slice. A partial that does
 * not finish the milestone does not lock change-order folding.
 */
export function invoiceClosesMilestone(invoice: MilestoneInvoiceRef | null | undefined): boolean {
  if (!invoice || invoiceIsVoid(invoice.status)) return false;
  if (invoice.closesMilestone === true) return true;
  if (invoice.partialId) return false;
  if (invoice.structureIds && invoice.structureIds.length > 0) return false;
  return true;
}

export function activeMilestoneInvoices(
  invoices: MilestoneInvoiceRef[] | null | undefined,
  installmentId: string,
): MilestoneInvoiceRef[] {
  return (invoices || []).filter(
    (invoice) => invoice?.installmentId === installmentId && !invoiceIsVoid(invoice.status),
  );
}

export function milestoneIsFullyInvoiced(
  invoices: MilestoneInvoiceRef[] | null | undefined,
  installmentId: string,
): boolean {
  return activeMilestoneInvoices(invoices, installmentId).some((invoice) => invoiceClosesMilestone(invoice));
}

export function coveredStructureIds(
  invoices: MilestoneInvoiceRef[] | null | undefined,
  installmentId: string,
  allStructureIds: string[],
): Set<string> {
  const active = activeMilestoneInvoices(invoices, installmentId);
  if (active.some((invoice) => invoiceClosesMilestone(invoice))) return new Set(allStructureIds);
  const covered = new Set<string>();
  for (const invoice of active) {
    for (const id of invoice.structureIds || []) {
      if (id) covered.add(id);
    }
  }
  return covered;
}

export function billedMilestoneAmount(
  invoices: MilestoneInvoiceRef[] | null | undefined,
  installmentId: string,
): number {
  return activeMilestoneInvoices(invoices, installmentId).reduce(
    (sum, invoice) => sum + Math.round(Number(invoice.amount) || 0),
    0,
  );
}

export function buildBillablePackages(
  groups: PackageGroupInput[],
  structures: ContractStructure[],
): BillablePackage[] {
  const known = new Map(structures.map((structure) => [structure.structureId, structure]));
  const membership = new Map<string, number>();
  const drafted: Array<{ key: string; label: string; structureIds: string[] }> = [];

  for (const group of groups) {
    if (!group.key || group.key === WHOLE_PROJECT_PACKAGE_KEY) continue;
    const structureIds = Array.from(
      new Set((group.structureIds || []).filter((id) => known.has(id))),
    );
    if (structureIds.length === 0) continue;
    drafted.push({
      key: group.key,
      label: (group.label || '').trim() || 'Structure group',
      structureIds,
    });
    for (const id of structureIds) membership.set(id, (membership.get(id) || 0) + 1);
  }

  return drafted.map((group) => ({
    key: group.key,
    label: group.label,
    structureIds: group.structureIds,
    contractValue: group.structureIds.reduce((sum, id) => sum + (known.get(id)?.contractValue || 0), 0),
    sharedStructureIds: group.structureIds.filter((id) => (membership.get(id) || 0) > 1),
  }));
}

export function unbilledPackages(
  packages: BillablePackage[],
  covered: Set<string>,
): BillablePackage[] {
  return packages
    .map((pkg) => {
      const structureIds = pkg.structureIds.filter((id) => !covered.has(id));
      if (structureIds.length === 0) return null;
      return { ...pkg, structureIds };
    })
    .filter((pkg): pkg is BillablePackage => !!pkg);
}

/** Structures on a package that may be included on this milestone's invoice. */
export function selectableStructureIds(args: {
  structureIds: string[];
  installmentId: PartialInstallmentId;
  coveredOnThisMilestone: Set<string>;
  coveredOnPreShipment: Set<string>;
}): string[] {
  return args.structureIds.filter((id) => {
    if (args.coveredOnThisMilestone.has(id)) return false;
    if (args.installmentId === 'final' && !args.coveredOnPreShipment.has(id)) return false;
    return true;
  });
}

export function selectionFromPackages(
  packages: BillablePackage[],
  selectedKeys: string[],
  /**
   * When every listed group is selected, structures in no group ride along so
   * the invoice can close the milestone. Structures already on this milestone,
   * including ones whose group was billed earlier, stay off.
   */
  orphans?: {
    allPackages: Array<{ structureIds: string[] }>;
    allStructureIds: string[];
    coveredStructureIds?: string[];
  },
): { structureIds: string[]; labels: string[]; keys: string[]; sharedStructureIds: string[] } {
  const chosen = packages.filter((pkg) => selectedKeys.includes(pkg.key));
  const counts = new Map<string, number>();
  for (const pkg of chosen) {
    for (const id of pkg.structureIds) counts.set(id, (counts.get(id) || 0) + 1);
  }
  const structureIds: string[] = [];
  const seen = new Set<string>();
  for (const pkg of chosen) {
    for (const id of pkg.structureIds) {
      if (seen.has(id)) continue;
      seen.add(id);
      structureIds.push(id);
    }
  }
  const selectingAll = packages.length > 0 && packages.every((pkg) => selectedKeys.includes(pkg.key));
  if (selectingAll && orphans) {
    const claimed = new Set(orphans.allPackages.flatMap((pkg) => pkg.structureIds));
    const covered = new Set(orphans.coveredStructureIds || []);
    for (const id of orphans.allStructureIds) {
      if (!id || claimed.has(id) || seen.has(id) || covered.has(id)) continue;
      seen.add(id);
      structureIds.push(id);
    }
  }
  return {
    structureIds,
    labels: chosen.map((pkg) => pkg.label),
    keys: chosen.map((pkg) => pkg.key),
    sharedStructureIds: Array.from(counts.entries())
      .filter(([, count]) => count > 1)
      .map(([id]) => id),
  };
}

function sameIdSet(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((id) => set.has(id));
}

function partialIdFor(installmentId: string, structureIds: string[]): string {
  return `${installmentId}:${[...structureIds].sort().join('|')}`;
}

export function partialInvoiceMemo(args: {
  milestoneLabel: string;
  invoiceAmount: number;
  milestoneAmount: number;
  includedLabels: string[];
  omittedLabels: string[];
  closesMilestone: boolean;
}): string {
  const included = args.includedLabels.filter(Boolean);
  const omitted = args.omittedLabels.filter(Boolean);
  const who = included.length ? included.join(', ') : 'the selected packages';
  const amount = formatInvoiceDollars(args.invoiceAmount);
  const full = formatInvoiceDollars(args.milestoneAmount);
  const scope = omitted.length
    ? `Partial invoice: ${args.milestoneLabel} for ${who} only.`
    : args.closesMilestone
      ? `Remaining ${args.milestoneLabel} for ${who}.`
      : `Partial invoice: ${args.milestoneLabel} for ${who}.`;
  const comparison = `This invoice is ${amount} of the ${full} ${args.milestoneLabel}.`;
  const leftOut = omitted.length ? ` Not included: ${omitted.join(', ')}.` : '';
  return `${scope} ${comparison}${leftOut}`;
}

/**
 * Keep change-order line amounts and put any rounding difference on the
 * structure lines so the invoice total is exact.
 */
export function composeSliceLines(
  coLines: SliceCoAdjustment[],
  target: number,
): { baseLineTarget: number; coLines: SliceCoAdjustment[] } {
  const kept = coLines
    .map((line) => ({ ...line, amount: Math.round(line.amount) }))
    .filter((line) => line.amount !== 0);
  const coSum = kept.reduce((sum, line) => sum + line.amount, 0);
  return {
    baseLineTarget: Math.round(target) - coSum,
    coLines: kept,
  };
}

function invoiceBaseCollected(invoice: MilestoneInvoiceRef): number {
  if (typeof invoice.baseBilled === 'number' && Number.isFinite(invoice.baseBilled)) {
    return Math.round(invoice.baseBilled);
  }
  return Math.round(invoice.amount || 0);
}

/** Change orders on this milestone that are not already on an active invoice. */
export function unbilledChangeOrders(
  adjustments: SliceCoAdjustment[] | null | undefined,
  invoices: MilestoneInvoiceRef[] | null | undefined,
  installmentId: string,
): SliceCoAdjustment[] {
  const billed = new Set<string>();
  for (const invoice of activeMilestoneInvoices(invoices, installmentId)) {
    for (const ref of invoice.foldedChangeOrderRefs || []) {
      const trimmed = String(ref || '').trim();
      if (trimmed) billed.add(trimmed);
    }
  }
  return (adjustments || []).filter(
    (line) => Math.round(line.amount) !== 0 && !billed.has(line.reference),
  );
}

export function allocateMilestoneSlice(args: AllocateMilestoneSliceArgs): AllocateMilestoneSliceResult {
  if (args.installmentId === 'deposit' && args.selectedStructureIds.length > 0) {
    return { ok: false, error: 'The deposit is one invoice for the whole contract.' };
  }

  const structures = args.structures.filter((structure) => structure.structureId);
  const allIds = structures.map((structure) => structure.structureId);
  const byId = new Map(structures.map((structure) => [structure.structureId, structure]));
  const milestoneAmount = Math.round(args.milestoneAmount);
  const active = activeMilestoneInvoices(args.existingInvoices, args.installmentId);

  if (active.some((invoice) => invoiceClosesMilestone(invoice))) {
    return { ok: false, error: `The ${args.milestoneLabel} milestone has already been invoiced in full.` };
  }

  const covered = coveredStructureIds(args.existingInvoices, args.installmentId, allIds);
  const billedAmount = billedMilestoneAmount(args.existingInvoices, args.installmentId);
  const requested = Array.from(new Set(args.selectedStructureIds.filter(Boolean)));
  const openChangeOrders = unbilledChangeOrders(args.coAdjustments, args.existingInvoices, args.installmentId);
  const explicitChangeOrders = Array.isArray(args.selectedChangeOrderRefs);
  const wantedRefs = new Set(
    explicitChangeOrders
      ? args.selectedChangeOrderRefs!.map((ref) => String(ref || '').trim()).filter(Boolean)
      : openChangeOrders.map((line) => line.reference),
  );
  const selectedChangeOrders = openChangeOrders
    .filter((line) => wantedRefs.has(line.reference))
    .map((line) => ({ ...line, amount: Math.round(line.amount) }))
    .filter((line) => line.amount !== 0);
  const unknownChangeOrders = explicitChangeOrders
    ? Array.from(wantedRefs).filter((ref) => !openChangeOrders.some((line) => line.reference === ref))
    : [];
  if (unknownChangeOrders.length) {
    return { ok: false, error: `${unknownChangeOrders.join(', ')} is not an open change order on this milestone.` };
  }
  const everyOpenChangeOrderSelected = openChangeOrders.every((line) => wantedRefs.has(line.reference));
  const allStructuresSelected = requested.length > 0 && sameIdSet(requested, allIds);
  const wholeMilestone = billedAmount === 0
    && covered.size === 0
    && everyOpenChangeOrderSelected
    && (requested.length === 0 || allStructuresSelected);

  if (wholeMilestone) {
    return {
      ok: true,
      slice: {
        fullMilestone: true,
        closesMilestone: true,
        structureIds: [],
        structureGroupKeys: [],
        structureGroupLabels: [],
        basisAmount: structures.reduce((sum, structure) => sum + structure.contractValue, 0),
        amount: milestoneAmount,
        baseLineTarget: Math.round(args.baseAmount),
        coLines: (args.coAdjustments || []).filter((line) => Math.round(line.amount) !== 0),
        includedLabels: [],
        omittedLabels: [],
      },
    };
  }

  if (requested.length === 0) {
    if (!explicitChangeOrders || selectedChangeOrders.length === 0) {
      return {
        ok: false,
        error: billedAmount > 0
          ? 'Choose the structure groups that are still unbilled.'
          : 'Choose a structure group or a change order.',
      };
    }
    const structuresLeft = structures.some((structure) => !covered.has(structure.structureId));
    const coSum = selectedChangeOrders.reduce((sum, line) => sum + line.amount, 0);
    const labels = selectedChangeOrders.map((line) => `${line.reference} ${line.title}`.trim());
    return {
      ok: true,
      slice: {
        fullMilestone: false,
        closesMilestone: !structuresLeft && everyOpenChangeOrderSelected,
        partialId: partialIdFor(args.installmentId, selectedChangeOrders.map((line) => line.reference)),
        structureIds: [],
        structureGroupKeys: [],
        structureGroupLabels: labels,
        basisAmount: 0,
        amount: coSum,
        baseLineTarget: 0,
        coLines: selectedChangeOrders,
        includedLabels: labels,
        omittedLabels: (args.omittedLabels || []).filter(Boolean),
      },
      memo: partialInvoiceMemo({
        milestoneLabel: args.milestoneLabel,
        invoiceAmount: coSum,
        milestoneAmount,
        includedLabels: labels,
        omittedLabels: (args.omittedLabels || []).filter(Boolean),
        closesMilestone: !structuresLeft && everyOpenChangeOrderSelected,
      }),
    };
  }

  const unknown = requested.filter((id) => !byId.has(id));
  if (unknown.length) {
    return { ok: false, error: 'One of the selected structures is not on this contract.' };
  }
  const already = requested.filter((id) => covered.has(id));
  if (already.length) {
    const names = already.map((id) => byId.get(id)?.structureName || id).join(', ');
    return { ok: false, error: `${names} is already on a ${args.milestoneLabel} invoice.` };
  }

  const unbilled = structures.filter((structure) => !covered.has(structure.structureId));
  const selected = requested.map((id) => byId.get(id)!);
  const remainingBase = Math.round(args.baseAmount) - active.reduce(
    (sum, invoice) => sum + invoiceBaseCollected(invoice),
    0,
  );
  if (remainingBase <= 0) {
    return { ok: false, error: `Nothing is left to bill on ${args.milestoneLabel}.` };
  }

  const selectedSet = new Set(requested);
  const isLast = unbilled.every((structure) => selectedSet.has(structure.structureId));
  const pool = isLast ? selected : unbilled;
  const structureTarget = Math.max(0, remainingBase);
  const poolWeights = pool.map((structure) => Math.max(0, structure.contractValue));
  const poolShares = distributeToTarget(poolWeights, structureTarget);
  const shareById = new Map(pool.map((structure, index) => [structure.structureId, poolShares[index] || 0]));
  const structureAmount = isLast
    ? structureTarget
    : requested.reduce((sum, id) => sum + (shareById.get(id) || 0), 0);
  const coLines = selectedChangeOrders;
  const amount = structureAmount + coLines.reduce((sum, line) => sum + line.amount, 0);
  if (amount <= 0) {
    return { ok: false, error: 'Selected packages have no contract value left to bill.' };
  }
  const composed = composeSliceLines(coLines, amount);
  const closesMilestone = isLast && everyOpenChangeOrderSelected;

  const includedLabels = (args.includedLabels || []).filter(Boolean);
  const labels = includedLabels.length
    ? includedLabels
    : selected.map((structure) => structure.structureName);
  const leftOutChangeOrders = openChangeOrders
    .filter((line) => !wantedRefs.has(line.reference))
    .map((line) => `${line.reference} ${line.title}`.trim());
  const omittedLabels = [...(args.omittedLabels || []).filter(Boolean), ...leftOutChangeOrders];
  const slice: MilestoneSlice = {
    fullMilestone: false,
    closesMilestone,
    partialId: partialIdFor(args.installmentId, requested),
    structureIds: requested,
    structureGroupKeys: args.includedGroupKeys || [],
    structureGroupLabels: labels,
    basisAmount: selected.reduce((sum, structure) => sum + structure.contractValue, 0),
    amount,
    baseLineTarget: composed.baseLineTarget,
    coLines: composed.coLines,
    includedLabels: labels,
    omittedLabels,
  };
  return {
    ok: true,
    slice,
    memo: partialInvoiceMemo({
      milestoneLabel: args.milestoneLabel,
      invoiceAmount: amount,
      milestoneAmount,
      includedLabels: labels,
      omittedLabels,
      closesMilestone,
    }),
  };
}

export function blockingInvoiceReason(
  invoices: MilestoneInvoiceRef[] | null | undefined,
  installmentId: string,
  requestedStructureIds: string[],
  options?: { changeOrderOnly?: boolean },
): string | null {
  const active = activeMilestoneInvoices(invoices, installmentId);
  if (installmentId === 'deposit') {
    return active.length ? 'The deposit installment has already been invoiced for this sales order.' : null;
  }
  if (active.some((invoice) => invoiceClosesMilestone(invoice))) {
    return `The ${installmentId} installment has already been invoiced in full for this sales order.`;
  }
  if (requestedStructureIds.length === 0) {
    if (options?.changeOrderOnly) return null;
    if (active.length) {
      return `The ${installmentId} installment already has a partial invoice. Choose the remaining structure groups.`;
    }
    return null;
  }
  const requested = new Set(requestedStructureIds);
  for (const invoice of active) {
    for (const id of invoice.structureIds || []) {
      if (requested.has(id)) {
        return `A ${installmentId} invoice already includes one of those structures.`;
      }
    }
  }
  return null;
}

export function depositIsInvoiced(
  invoices: MilestoneInvoiceRef[] | null | undefined,
  legacyDeposit: boolean,
): boolean {
  if (legacyDeposit) return true;
  return activeMilestoneInvoices(invoices, 'deposit').length > 0;
}

export function structuresMissingPreShipment(
  structureIds: string[],
  preShipmentInvoices: MilestoneInvoiceRef[] | null | undefined,
  allStructureIds: string[],
): string[] {
  const covered = coveredStructureIds(preShipmentInvoices, 'pre_shipment', allStructureIds);
  return structureIds.filter((id) => !covered.has(id));
}

/** Firestore map key for the create lock. Dots are flattened so the path stays one field. */
export function invoiceLockKey(installmentId: string, structureIds: string[]): string {
  if (!structureIds.length) return installmentId;
  const safe = [...structureIds].sort().join('_').replace(/[^A-Za-z0-9_]/g, '_');
  return `${installmentId}__${safe}`.slice(0, 400);
}

export interface MilestonePackageChoice {
  key: string;
  label: string;
  structureIds: string[];
  contractValue: number;
  sharedStructureIds: string[];
  disabledReason?: string;
}

/**
 * Deposit, and a job with a single unbilled package, invoice without a picker.
 * Several unbilled packages open the picker. One remaining package invoices
 * that package directly.
 */
export function milestonePackageChoices(args: {
  installmentId: PartialInstallmentId;
  packages: BillablePackage[];
  structures: ContractStructure[];
  invoices: MilestoneInvoiceRef[];
  /** Folded change orders on this milestone. When any are still open, the picker stays open. */
  changeOrders?: SliceCoAdjustment[];
}): { mode: 'full' | 'single' | 'pick'; packages: MilestonePackageChoice[]; structureIds: string[] } {
  if (args.installmentId === 'deposit' || milestoneIsFullyInvoiced(args.invoices, args.installmentId)) {
    return { mode: 'full', packages: [], structureIds: [] };
  }
  const allIds = args.structures.map((structure) => structure.structureId);
  const coveredHere = coveredStructureIds(args.invoices, args.installmentId, allIds);
  const coveredPre = coveredStructureIds(args.invoices, 'pre_shipment', allIds);
  const choices: MilestonePackageChoice[] = args.packages.map((pkg) => {
    const selectable = selectableStructureIds({
      structureIds: pkg.structureIds,
      installmentId: args.installmentId,
      coveredOnThisMilestone: coveredHere,
      coveredOnPreShipment: coveredPre,
    });
    const fullyBilledHere = pkg.structureIds.length > 0
      && pkg.structureIds.every((id) => coveredHere.has(id));
    const disabledReason = fullyBilledHere
      ? 'Already on this invoice.'
      : args.installmentId === 'final' && selectable.length === 0
        ? 'Bill the pre-shipment invoice for this package first.'
        : undefined;
    return {
      key: pkg.key,
      label: pkg.label,
      structureIds: disabledReason ? pkg.structureIds : selectable,
      contractValue: pkg.contractValue,
      sharedStructureIds: pkg.sharedStructureIds,
      disabledReason,
    };
  });
  const enabled = choices.filter((choice) => !choice.disabledReason && choice.structureIds.length > 0);
  const openChangeOrders = unbilledChangeOrders(args.changeOrders, args.invoices, args.installmentId);
  if (enabled.length === 0) {
    if (openChangeOrders.length > 0) return { mode: 'pick', packages: choices, structureIds: [] };
    return { mode: 'full', packages: choices, structureIds: [] };
  }
  const claimed = new Set(choices.flatMap((choice) => choice.structureIds));
  const outsideGroups = allIds.filter((id) => !claimed.has(id));
  if (enabled.length === 1 && openChangeOrders.length === 0) {
    const only = enabled[0];
    const structureIds = Array.from(new Set([...only.structureIds, ...outsideGroups]));
    const nothingBilled = billedMilestoneAmount(args.invoices, args.installmentId) === 0;
    if (nothingBilled && sameIdSet(structureIds, allIds)) {
      return { mode: 'full', packages: choices, structureIds: [] };
    }
    return { mode: 'single', packages: choices, structureIds };
  }
  return { mode: 'pick', packages: choices, structureIds: [] };
}
